/**
 * NakshatraAngaCalculator - Lazy calculator for Nakshatra Anga table data
 * Provides async calculation of planets by nakshatra with progress tracking
 */

import { devLog, devWarn } from '../../../../../utils/devLog';
import { JatakaResponse } from '../../../../../models/api';
import {
    NAKSHATRA_ANGA_ATTRIBUTES,
    getNakshatraIndexFromLongitude,
    parseLongitudeFromPosition
} from '../data/NakshatraAngaTableData';
import { lazyCalculator } from '../lazy/SimpleLazyCalculator';

/**
 * Enhanced planet display with retrograde and special highlighting
 */
interface PlanetDisplay {
    shortName: string;
    isRetrograde: boolean;
    isHora: boolean;
    isChandra: boolean;
    isLagna: boolean;
    displayText: string;
    longitude: number; // For debugging
    nakshatraIndex: number; // For debugging
}

/**
 * Result of Nakshatra Anga calculation
 */
export interface NakshatraAngaResult {
    planetsByNakshatra: Record<number, PlanetDisplay[]>;
    totalPlanets: number;
    calculationTime: number;
    nakshatraCount: number;
    chandraNakshatraIndex: number | null; // Chandra's nakshatra for Tara Phala calculation
}

/**
 * Calculate planets by nakshatra with progress tracking
 */
async function calculateNakshatraAnga(
    apiResponse: JatakaResponse
): Promise<NakshatraAngaResult> {
    const startTime = Date.now();


    // Initialize result structure
    const planetsByNakshatra: Record<number, PlanetDisplay[]> = {};

    // Initialize all nakshatras with empty arrays (1-27)
    for (let i = 1; i <= 27; i++) {
        planetsByNakshatra[i] = [];
    }

    // Track Chandra's nakshatra for Tara Phala calculation
    let chandraNakshatraIndex: number | null = null;

    // Get current hora planet for highlighting (same as Graha Chakra)
    const currentHoraPlanet = apiResponse.detailed_panchanga?.hora_details?.current_planet;
    const horaPlanetName = currentHoraPlanet ? currentHoraPlanet.split(' ')[0] : null;


    // Process each planet INCLUDING LAGNA
    if (apiResponse.graha_positions?.planets) {
        const planets = apiResponse.graha_positions.planets;
        const _totalPlanets = planets.length;

        for (let i = 0; i < planets.length; i++) {
            const planet = planets[i];


            // Parse longitude from position (contains global degrees)
            const longitude = parseLongitudeFromPosition(planet.position);

            // Skip if longitude parsing failed
            if (longitude === null) {
                devWarn(`⚠️ NakshatraAngaCalculator: Failed to parse longitude for ${planet.name}`);
                continue;
            }

            // Determine which nakshatra this planet falls into (1-27)
            const nakshatraIndex = getNakshatraIndexFromLongitude(longitude);

            // Determine planet special properties
            const isLagna = planet.short_name === 'Lg';
            const isChandra = planet.short_name === 'Ch';
            const isRetrograde = planet.is_retrograde || false;

            // Track Chandra's nakshatra for Tara Phala calculation
            if (isChandra) {
                chandraNakshatraIndex = nakshatraIndex;
                devLog(`🌙 Chandra found in nakshatra ${nakshatraIndex} (${longitude.toFixed(2)}°) - Tara Phala reference point set`);
            }

            const isHora = !!(horaPlanetName && (
                planet.short_name === horaPlanetName ||
                planet.name === horaPlanetName
            ));

            // Create display text: Sy for normal, (Sy) for retrograde
            let displayText = planet.short_name;
            if (isRetrograde) {
                displayText = `(${planet.short_name})`;
            }

            const planetDisplay: PlanetDisplay = {
                shortName: planet.short_name,
                isRetrograde,
                isHora,
                isChandra,
                isLagna,
                displayText,
                longitude,
                nakshatraIndex
            };

            // Add to the appropriate nakshatra group
            planetsByNakshatra[nakshatraIndex].push(planetDisplay);

            devLog(`🌟 Nakshatra Anga Calculator: ${planet.name} (${planet.short_name}) at ${longitude.toFixed(2)}° → Nakshatra ${nakshatraIndex}`);
        }
    }


    // Simulate some processing time for demonstration
    await new Promise(resolve => setTimeout(resolve, 50));

    // Simulate processing time for realistic async behavior
    await new Promise(resolve => setTimeout(resolve, 30));

    const calculationTime = Date.now() - startTime;
    const totalPlanets = apiResponse.graha_positions?.planets?.length || 0;

    const result: NakshatraAngaResult = {
        planetsByNakshatra,
        totalPlanets,
        calculationTime,
        nakshatraCount: NAKSHATRA_ANGA_ATTRIBUTES.length,
        chandraNakshatraIndex
    };

    devLog(`✅ NakshatraAngaCalculator: Completed in ${calculationTime}ms - processed ${totalPlanets} planets across ${result.nakshatraCount} nakshatras`);
    if (chandraNakshatraIndex) {
        devLog(`🌙 Chandra nakshatra: ${chandraNakshatraIndex} - Tara Phala calculations ready`);
    } else {
        devWarn(`⚠️ Chandra not found - Tara Phala will show N/A`);
    }

    return result;
}

/**
 * Generate cache key for Nakshatra Anga calculation
 */
function generateCacheKey(apiResponse: JatakaResponse): string {
    if (!apiResponse.graha_positions?.planets) {
        return 'no-planets';
    }

    // Create cache key from planet positions and hora
    const planetPositions = apiResponse.graha_positions.planets
        .map(p => `${p.short_name}:${p.position}:${p.is_retrograde ? 'R' : 'D'}`)
        .join('|');

    const horaInfo = apiResponse.detailed_panchanga?.hora_details?.current_planet || 'no-hora';

    return `${planetPositions}|hora:${horaInfo}`;
}

// Register the lazy calculator
lazyCalculator.register({
    id: 'nakshatra-anga',
    calculator: calculateNakshatraAnga,
    cacheKey: generateCacheKey,
    ttl: 600000 // 10 minutes - manual invalidation when positions change
});

export { calculateNakshatraAnga, generateCacheKey };